import { FC } from "react";

import {
  FormTitle,
  FormWrapper,
} from "@/components/Forms/components/MonolithFormComponents";
import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar, VariantType } from "notistack";
import LoggingAPI from "@/api/logging";
import {
  DefaultAcquisitionForm,
  AcquisitionFormData,
  EditAcquisitionFormData,
} from "./DefaultAcqForm";
import { Acquisition } from "@/types";
import { updateAcquisition } from "@/api/acquisitions";
import { db_timestamp } from "@/utils/date-format";
import { useCustomFields, CustomField } from "./hooks/useCustomFields";
import { arraysEqual } from "./utils/customAttributeUtils";
import { safeParseJSON } from "@/utils/File-parsers/safeParseJSON";
import {
  FormDataWithCustomAttributes,
  ApiCustomAttribute,
} from "../acquisitions.types";

interface EditAcquisitionFormProps {
  width?: number | undefined;
  defaultFormData: Acquisition;
  onSubmit: () => void;
  onCancel: () => void;
  caseInfo: { case_id: number };
  showEvidenceSelector?: boolean;
}

export const EditAcquisitionForm: FC<EditAcquisitionFormProps> = ({
  width,
  defaultFormData,
  onSubmit,
  onCancel,
  caseInfo,
  showEvidenceSelector,
}) => {
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  // Fetch custom fields for acquisitions
  const { data: customFields } = useCustomFields();

  const handleSubmit = async (
    data: AcquisitionFormData,
    diffData?: EditAcquisitionFormData
  ) => {
    try {
      if (defaultFormData.acq_id) {
        // Format data similarly to how it's done in the popup
        const updateData: FormDataWithCustomAttributes = { ...diffData };

        // Convert storage_id to proper format for edit API
        if (updateData.storage_id !== undefined) {
          if (updateData.storage_id) {
            const storageId =
              typeof updateData.storage_id === "string"
                ? parseInt(updateData.storage_id)
                : updateData.storage_id;
            updateData.storage_id = storageId; // Keep as storage_id for edit API
          } else {
            updateData.storage_id = null; // Clear storage
          }
          // Don't convert to storage_items for edit operations
        }

        // Format date if it's changed
        if (
          updateData.acquire_date &&
          typeof updateData.acquire_date === "string"
        ) {
          updateData.acquire_date = db_timestamp(updateData.acquire_date);
        }

        // Handle linked contact updates (as in the popup)
        if (
          updateData.contact &&
          typeof updateData.contact === "object" &&
          updateData.contact !== null &&
          "client_id" in updateData.contact
        ) {
          const contact = updateData.contact as { client_id: number | null };
          if (contact.client_id === null) {
            // This would be handling the contact creation case, but we don't have that functionality
            // in our simplified version. In the original, it creates a new contact.
            delete updateData.contact;
          } else {
            updateData.person_id = contact.client_id;
            delete updateData.contact;
          }
        }

        // Handle custom attributes
        const customAttributes: ApiCustomAttribute[] = [];
        for (const key in updateData) {
          if (key.indexOf("custom_attribute_") !== -1) {
            const field_id = parseInt(key.replace("custom_attribute_", ""));
            const currentAttr = defaultFormData.custom_attributes?.find(
              (attr) => attr.field_id === field_id
            );

            // Compare values properly, especially for TagBox arrays
            let valuesAreEqual = false;
            if (currentAttr) {
              const currentValue = safeParseJSON(currentAttr.value);
              const newValue = safeParseJSON(String(updateData[key] || ""));
              if (Array.isArray(currentValue) && Array.isArray(newValue)) {
                valuesAreEqual = arraysEqual(currentValue, newValue);
              } else {
                valuesAreEqual = currentValue === newValue;
              }
            }

            // Skip if the value hasn't actually changed
            if (valuesAreEqual) {
              delete updateData[key];
              continue;
            }

            // Find the base attribute definition to get field_name
            const baseAttr = customFields?.find(
              (attr: CustomField) => attr.field_id === field_id
            );

            customAttributes.push({
              field_id: field_id,
              field_name:
                baseAttr?.field_name ||
                currentAttr?.field_name ||
                `Field ${field_id}`,
              item_id: defaultFormData.acq_id,
              value: updateData[key] as string | number | string[] | null,
            });
            delete updateData[key];
          }
        }

        if (customAttributes.length > 0) {
          updateData.custom_attributes = customAttributes;
        }

        // Only update if there are changes
        if (Object.keys(updateData).length > 0) {
          await updateAcquisition(defaultFormData.acq_id, updateData);
          onSubmit?.();

          enqueueSnackbar(`Acquisition ${data?.acq_name || ""} updated`, {
            variant: "success" as VariantType,
          });

          LoggingAPI.createLog({
            user_id: currentUser.user_id,
            message: `Edited Acquisition: ${data?.acq_name}`,
            event: "acquisition:edit",
            object_type: "acquisition",
          });
        } else {
          // No changes to update
          enqueueSnackbar(`No new data to update.`, {
            variant: "warning" as VariantType,
          });
          onCancel();
        }
      } else {
        enqueueSnackbar(
          `An error occurred while editing acquisition ${
            data?.acq_name || ""
          }. Please try again.`,
          { variant: "error" as VariantType }
        );
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        `An error occurred while editing acquisition ${
          data?.acq_name || ""
        }. Please try again.`,
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <FormWrapper width={width}>
      <FormTitle>Edit Acquisition</FormTitle>
      <DefaultAcquisitionForm
        onSubmit={handleSubmit}
        onCancel={onCancel}
        DefaultFormData={{
          ...defaultFormData,
          acq_tool: defaultFormData.acq_tool,
          storage_id: defaultFormData.storage_id
            ? defaultFormData.storage_id.toString()
            : undefined,
        }}
        caseId={caseInfo?.case_id}
        showEvidenceSelector={showEvidenceSelector}
        buttonProps={{ submitText: "Save Changes" }}
      />
    </FormWrapper>
  );
};
